/**
 * Patients API Endpoints
 * Handles patient-related API calls
 */

import { axiosInstance } from '../axios.config';
import type {
  Patient,
  PatientListResponse,
  PatientQueryParams,
  CreatePatientData,
  UpdatePatientData,
} from '@/types/patient.types';
import { uploadDocument } from '../../utils/documentUpload.utils';
import type { UploadResponseData } from '@/types/upload.types';

/**
 * Patients API
 */
export const patientsApi = {
  /**
   * Convert referral into a patient record
   */
  convertFromReferral: async (referralId: string, data: Record<string, unknown>): Promise<Patient> => {
    const response = await axiosInstance.post<Patient>(`/api/v1/patients/convert-from-referral/${referralId}`, data);
    return response.data;
  },

  /**
   * Convert service request into a patient record
   */
  convertFromServiceRequest: async (requestId: string, data: Record<string, unknown>): Promise<Patient> => {
    const response = await axiosInstance.post<Patient>(`/api/v1/patients/convert-from-service-request/${requestId}`, data);
    return response.data;
  },
  getEpisodes: async (patientId: string): Promise<any[]> => {
    const response = await axiosInstance.get(`/api/v1/patients/${patientId}/episodes`);
    return response.data?.data || response.data || [];
  },
  activateEpisode: async (episodeId: string): Promise<any> => {
    const response = await axiosInstance.post(`/api/v1/patients/episodes/${episodeId}/activate`);
    return response.data?.data || response.data;
  },
  dischargeEpisode: async (
    episodeId: string,
    data: { dischargeReason?: string; dischargeNotes?: string },
  ): Promise<any> => {
    const response = await axiosInstance.post(`/api/v1/patients/episodes/${episodeId}/discharge`, data);
    return response.data?.data || response.data;
  },
  reopenEpisode: async (episodeId: string): Promise<any> => {
    const response = await axiosInstance.post(`/api/v1/patients/episodes/${episodeId}/reopen`);
    return response.data?.data || response.data;
  },
  getEpisodeCareTeam: async (episodeId: string): Promise<any> => {
    const response = await axiosInstance.get(`/api/v1/patients/episodes/${episodeId}/care-team`);
    return response.data?.data || response.data;
  },
  setEpisodeCareTeam: async (
    episodeId: string,
    members: Array<{ staff: string; role?: string; discipline?: string; notes?: string }>,
  ): Promise<{ data: any; meta?: any }> => {
    const response = await axiosInstance.put(`/api/v1/patients/episodes/${episodeId}/care-team`, {
      members,
    });
    return { data: response.data?.data || response.data, meta: response.data?.meta };
  },
  addEpisodeCareTeamMember: async (
    episodeId: string,
    member: { staff: string; role?: string; discipline?: string; notes?: string },
  ): Promise<any> => {
    const response = await axiosInstance.post(
      `/api/v1/patients/episodes/${episodeId}/care-team/members`,
      member,
    );
    return response.data?.data || response.data;
  },
  removeEpisodeCareTeamMember: async (episodeId: string, staffId: string): Promise<any> => {
    const response = await axiosInstance.delete(
      `/api/v1/patients/episodes/${episodeId}/care-team/members/${staffId}`,
    );
    return response.data?.data || response.data;
  },
  getPatientOpenCareTeam: async (patientId: string): Promise<any> => {
    const response = await axiosInstance.get(`/api/v1/patients/${patientId}/care-team`);
    return response.data?.data || response.data;
  },
  /**
   * Get paginated list of patients
   */
  getPatients: async (params?: PatientQueryParams): Promise<PatientListResponse> => {
    const response = await axiosInstance.get<PatientListResponse>('/api/v1/patients', { params });
    return response.data;
  },

  /**
   * Get single patient by ID
   */
  getPatientById: async (id: string): Promise<Patient> => {
    const response = await axiosInstance.get<Patient>(`/api/v1/patients/${id}`);
    return response.data;
  },

  /**
   * Create new patient
   */
  createPatient: async (data: CreatePatientData): Promise<Patient> => {
    const response = await axiosInstance.post<Patient>('/api/v1/patients', data);
    return response.data;
  },

  /**
   * Update existing patient
   */
  updatePatient: async (id: string, data: UpdatePatientData): Promise<Patient> => {
    const response = await axiosInstance.put<Patient>(`/api/v1/patients/${id}`, data);
    return response.data;
  },

  /**
   * Delete patient
   */
  deletePatient: async (id: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete<{ message: string }>(`/api/v1/patients/${id}`);
    return response.data;
  },

  /**
   * Update patient status
   */
  updateStatus: async (id: string, status: string): Promise<Patient> => {
    const response = await axiosInstance.put<Patient>(`/api/v1/patients/${id}/status`, { status });
    return response.data;
  },

  /**
   * Search patients
   */
  searchPatients: async (query: string): Promise<Patient[]> => {
    const response = await axiosInstance.get<Patient[]>('/api/v1/patients/search', {
      params: { q: query },
    });
    return response.data;
  },

  /**
   * Get patient medical history
   */
  getPatientHistory: async (id: string): Promise<any> => {
    const response = await axiosInstance.get(`/api/v1/patients/${id}/history`);
    return response.data;
  },

  /**
   * Get patient health records
   */
  getPatientHealthRecords: async (patientId: string): Promise<any> => {
    const response = await axiosInstance.get(`/health-records/patient/${patientId}`);
    return response.data;
  },

  /**
   * Bulk upload patients
   * Uses documentUpload utility to upload file to media service first
   */
  bulkUpload: async (file: File): Promise<{ message: string; count: number; uploadMetadata?: UploadResponseData }> => {
    // Upload file using documentUpload utility
    const folderPath = 'patients/bulk-upload';
    const uploadResult: UploadResponseData = await uploadDocument(file, { folderPath });
    
    // Send metadata to backend for processing
    const response = await axiosInstance.post<{ message: string; count: number }>(
      '/api/v1/patients/bulk-upload',
      {
        fileUrl: uploadResult.cdnUrl || uploadResult.baseUrl,
        fileName: uploadResult.fileName,
        metadata: uploadResult,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return { ...response.data, uploadMetadata: uploadResult };
  },
};

export default patientsApi;

